/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileArchive, ShieldCheck, FilePlus2, FileText, History, Pill } from 'lucide-react';
import ConsentCard from '@/components/fse/ConsentCard';
import DocumentCreator from '@/components/fse/DocumentCreator';
import DocumentList from '@/components/fse/DocumentList';
import AuditLog from '@/components/fse/AuditLog';
import PrescriptionCreator from '@/components/fse/PrescriptionCreator';

const FSELombardiaPage = () => {
  const { user } = useAuth();
  const [refreshTick, setRefreshTick] = useState(0);

  const isPatient = user?.category === 'paziente';
  const isPro = ['medici', 'infermieri'].includes(user?.category);
  const isDoctor = user?.category === 'medici';
  const bump = () => setRefreshTick((t) => t + 1);

  return (
    <div className="space-y-5 max-w-4xl" data-testid="fse-lombardia-page">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
          <FileArchive className="h-6 w-6 text-primary" /> FSE Lombardia
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Fascicolo Sanitario Elettronico — gestione consenso paziente, generazione documenti CDA2 e audit trail (mTLS simulato).
        </p>
      </div>

      {isPatient && (
        <Tabs defaultValue="consent" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="consent" data-testid="fse-tab-consent">
              <ShieldCheck className="h-4 w-4 mr-1" /> Consenso
            </TabsTrigger>
            <TabsTrigger value="docs" data-testid="fse-tab-docs">
              <FileText className="h-4 w-4 mr-1" /> Miei documenti
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="fse-tab-audit">
              <History className="h-4 w-4 mr-1" /> Audit
            </TabsTrigger>
          </TabsList>
          <TabsContent value="consent"><ConsentCard onChange={bump} /></TabsContent>
          <TabsContent value="docs"><DocumentList mode="patient" refreshTick={refreshTick} /></TabsContent>
          <TabsContent value="audit"><AuditLog refreshTick={refreshTick} /></TabsContent>
        </Tabs>
      )}

      {isPro && (
        <Tabs defaultValue="create" className="space-y-4">
          <TabsList className={`grid w-full ${isDoctor ? 'grid-cols-4' : 'grid-cols-3'}`}>
            <TabsTrigger value="create" data-testid="fse-tab-create">
              <FilePlus2 className="h-4 w-4 mr-1" /> Nuovo documento
            </TabsTrigger>
            {isDoctor && (
              <TabsTrigger value="prescription" data-testid="fse-tab-prescription">
                <Pill className="h-4 w-4 mr-1" /> Ricetta
              </TabsTrigger>
            )}
            <TabsTrigger value="docs" data-testid="fse-tab-docs-pro">
              <FileText className="h-4 w-4 mr-1" /> I miei documenti
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="fse-tab-audit-pro">
              <History className="h-4 w-4 mr-1" /> Audit
            </TabsTrigger>
          </TabsList>
          <TabsContent value="create"><DocumentCreator onCreated={bump} /></TabsContent>
          {isDoctor && (
            <TabsContent value="prescription"><PrescriptionCreator onCreated={bump} /></TabsContent>
          )}
          <TabsContent value="docs"><DocumentList mode="pro" refreshTick={refreshTick} onChanged={bump} /></TabsContent>
          <TabsContent value="audit"><AuditLog refreshTick={refreshTick} /></TabsContent>
        </Tabs>
      )}

      {!isPatient && !isPro && (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
          FSE riservato a pazienti e professionisti sanitari abilitati.
        </CardContent></Card>
      )}
    </div>
  );
};

export default FSELombardiaPage;
